import React from 'react';
import { useState, useRef } from 'react';
import { MonitorDownIcon } from 'lucide-react';
import { toast } from 'sonner';

export default function ScreenRecorder() {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [videoURL, setVideoURL] = useState<string | null>(null);
  const [downloadLink, setDownloadLink] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = async () => {
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
      });

      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: true,

      });

      const combinedStream = new MediaStream([
        ...screenStream.getVideoTracks(),
        ...audioStream.getAudioTracks(),
      ]);

      mediaRecorderRef.current = new MediaRecorder(combinedStream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setVideoURL(url);
        setDownloadLink(url);
      };

      const startSound = new Audio('/assets/audio/recording-in-progress.wav');
      await startSound.play();

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting screen recording:', error);
      // TODO: translate
      toast.error('Failed to start screen recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (downloadLink) {
        const link = document.createElement('a');
        link.href = downloadLink;
        link.download = `talksy-${new Date().toLocaleString()}.webm`;
        link.click();
        URL.revokeObjectURL(downloadLink);
      }
      // TODO: translate
      toast.success('Recording finished!');
    }
  };

  return (
    <div className="flex h-full">
      {isRecording ? (
        <button
          onClick={stopRecording}
          className="flex animate-pulse items-center rounded-lg bg-stone-900 p-2 font-bold text-red-500 hover:bg-red-500 hover:text-white"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="size-4"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <rect width="18" height="18" x="3" y="3" />
          </svg>
        </button>
      ) : (
        <button
          onClick={startRecording}
          className="flex items-center rounded-lg bg-stone-900 p-2 font-bold text-red-500 hover:bg-red-500 hover:text-white"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="size-4"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <circle cx="12" cy="12" r="10" fill="currentColor" />
          </svg>
        </button>
      )}

      {videoURL && (
        <div className="flex items-center pl-1">
          {downloadLink && (
            <a
              href={downloadLink}
              download={`talksy-${new Date().toLocaleString()}.webm`}
              className="font-sm flex h-full animate-pulse items-center rounded-lg bg-stone-900 px-4 py-2 text-white hover:bg-blue-500"
            >
              <MonitorDownIcon className="size-5" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}
